import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdminBusinesses, adminAction } from "@/services/adminApi";
import type { AdminBusiness } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
];

export default function AdminBusinesses() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdminBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [pendingReject, setPendingReject] = useState<AdminBusiness | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminBusinesses(token)
      .then((b) => active && setRows(b))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2400);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.owner.toLowerCase().includes(q) ||
        r.categoryName.toLowerCase().includes(q) ||
        r.locationName.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const toggleFeature = async (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const next = !row.isFeatured;
    const ok = await adminAction(`/admin/businesses/${id}/feature`, { featured: next }, token);
    if (!ok) {
      flashError("Couldn't update the featured status. Please try again.");
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isFeatured: next } : r)));
    flash("Featured status updated.");
  };

  const setRowStatus = async (id: string, next: AdminBusiness["status"]) => {
    const ok = await adminAction(`/admin/businesses/${id}/status`, { status: next }, token);
    if (!ok) {
      flashError("Couldn't update the listing status. Please try again.");
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: next } : r)));
    flash(`Listing marked as ${next}.`);
  };

  const columns: DataColumn<AdminBusiness>[] = [
    {
      key: "name",
      header: "Business",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-200 text-foreground-600">
            <i className="ri-store-2-line"></i>
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">/{r.slug}</p>
          </div>
        </div>
      ),
    },
    { key: "categoryName", header: "Category", render: (r) => <span className="text-foreground-700">{r.categoryName}</span>, nowrap: true },
    { key: "locationName", header: "Location", render: (r) => <span className="text-foreground-700">{r.locationName}</span>, nowrap: true },
    { key: "owner", header: "Owner", render: (r) => <span className="text-foreground-700">{r.owner}</span>, nowrap: true },
    {
      key: "plan",
      header: "Plan",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-700">
          {r.plan}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    {
      key: "featured",
      header: "Featured",
      align: "center",
      render: (r) => (
        <button
          type="button"
          onClick={() => toggleFeature(r.id)}
          aria-label={r.isFeatured ? "Remove from featured" : "Mark as featured"}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md cursor-pointer ${
            r.isFeatured ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-400 hover:text-foreground-700"
          }`}
        >
          <i className={r.isFeatured ? "ri-star-fill" : "ri-star-line"}></i>
        </button>
      ),
    },
    { key: "views", header: "Views", align: "right", render: (r) => <span className="text-foreground-700">{r.views.toLocaleString()}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <Link
            to={`/admin/businesses/${r.id}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit listing"
          >
            <i className="ri-edit-line"></i>
          </Link>
          <Link
            to={`/business/${r.slug}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="View profile"
          >
            <i className="ri-external-link-line"></i>
          </Link>
          {r.status !== "approved" && (
            <button
              type="button"
              onClick={() => setRowStatus(r.id, "approved")}
              className="inline-flex items-center gap-1 rounded-md border border-accent-300 px-2.5 py-1.5 text-xs font-semibold text-accent-800 hover:bg-accent-50 cursor-pointer"
            >
              <i className="ri-check-line"></i> Approve
            </button>
          )}
          {r.status !== "rejected" && (
            <button
              type="button"
              onClick={() => setPendingReject(r)}
              className="inline-flex items-center gap-1 rounded-md border border-secondary-300 px-2.5 py-1.5 text-xs font-semibold text-secondary-800 hover:bg-secondary-50 cursor-pointer"
            >
              <i className="ri-close-line"></i> Reject
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Manage Businesses" path="/admin/businesses" />

      <AdminPageHeader
        icon="ri-store-2-line"
        title="Businesses"
        subtitle="Every listing on the platform — approve, reject, feature or open a profile."
        actions={
          actionError ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
              <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
            </span>
          ) : notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search by name, owner, category…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <p className="mt-4 text-sm text-foreground-600">
        <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} listings
      </p>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load businesses. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="1080px" />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingReject)}
        title="Reject this listing?"
        message={pendingReject ? `“${pendingReject.name}” will be marked as rejected and hidden from the public directory.` : ""}
        confirmLabel="Reject listing"
        onConfirm={() => {
          const target = pendingReject;
          setPendingReject(null);
          if (target) void setRowStatus(target.id, "rejected");
        }}
        onCancel={() => setPendingReject(null)}
      />
    </>
  );
}